import {Action, handleActions} from 'redux-actions';
import {curry} from 'ramda';
import {
    CONTENT_ITEM_VIEW_METADATA_SET,
    CONTENT_ITEM_VIEW_METADATA_RESET,
} from './ContentItemViewMetadataActions';
import {
    ContentItemViewMetadataState,
    ContentItem,
    LanguageMetaItem,
    RoleMetaItem,
    MetadataTerritorial,
    MetadataLocalized,
    MetadataGeneral,
    Role,
    TerritorialMetaItem,
} from '../ContentItemViewMetadataTypes';
import {RoleMetadataType} from '../ContentItemViewMetadataGeneral';

interface Roles {
    actors: Role[];
    directors: Role[];
    producers: Role[];
    composers: Role[];
    writers: Role[];
    roleMeta: RoleMetadataType[];
}

enum RoleItems {
    actor = 'actor',
    director = 'director',
    producer = 'producer',
    composer = 'composer',
    writer = 'writer',
}

const getMetaType = curry((type: RoleItems, item: RoleMetaItem) => {
    return item.roleMetaType === type;
});

export const rolesMapper = ({id, label}: RoleMetaItem) => ({
    id,
    label,
});

export const getRoles = (roleMeta: RoleMetaItem[]): Roles => {
    const actors = roleMeta.filter(getMetaType(RoleItems.actor)).map(rolesMapper);
    const directors = roleMeta.filter(getMetaType(RoleItems.director)).map(rolesMapper);
    const producers = roleMeta.filter(getMetaType(RoleItems.producer)).map(rolesMapper);
    const composers = roleMeta.filter(getMetaType(RoleItems.composer)).map(rolesMapper);
    const writers = roleMeta.filter(getMetaType(RoleItems.writer)).map(rolesMapper);

    return {
        actors,
        directors,
        producers,
        composers,
        writers,
        roleMeta,
    };
};

export const getEnglishTitleOrDefault = (languageMeta: LanguageMetaItem[]): string => {
    const englishMeta = languageMeta.filter(
        (item: LanguageMetaItem) => item.language === 'en'
    );

    return englishMeta[0]?.title ?? languageMeta[0].title;
};

const getLanguages = (languageMeta: LanguageMetaItem[]): string[] => {
    return languageMeta.map((item: LanguageMetaItem) => item.language);
};

const getCountryCodes = (territorialMeta: TerritorialMetaItem[]): string[] => {
    return territorialMeta.map((item: TerritorialMetaItem) => item.countryCode);
};

const getLanguageProperties = (
    lang: string,
    languageMeta: LanguageMetaItem[]
): LanguageMetaItem[] => {
    return languageMeta.filter((item: LanguageMetaItem) => item.language === lang);
};

const getLocalizedObject = (
    languages: string[],
    languageMeta: LanguageMetaItem[]
): MetadataLocalized => {
    const localizedObject = {} as MetadataLocalized;
    for (const lang of languages) {
        const properties = getLanguageProperties(lang, languageMeta);
        const descriptor = {
            value: properties[0],
            enumerable: true,
        };
        Object.defineProperty(localizedObject, lang, descriptor);
    }

    return localizedObject;
};

export const getGeneralMetadata = (contentItem: ContentItem): MetadataGeneral => {
    const {
        languageMeta,
        contentPartner,
        roleMeta,
        copyrightHolder,
        contentItemGenres,
        contentItemSubGenres,
        copyrightYear,
        releaseDate,
        sourceRegion,
        number,
        seasonNumber,
        seriesTitle,
        displayTitle,
        mamId,
    } = contentItem;
    const title = getEnglishTitleOrDefault(languageMeta);
    const contentId = contentItem.id;
    const partner = contentPartner.partnerName;
    const partnerHash = contentPartner.cmsPartnerId;
    const availsId = contentItem.plutotvAvailsId;
    const contentType = contentItem.contentItemType;
    const roles = getRoles(roleMeta);

    return {
        title,
        contentId,
        partner,
        partnerHash,
        availsId,
        contentType,
        sourceRegion,
        releaseDate,
        copyrightHolder,
        copyrightYear,
        roles,
        episodeNumber: number,
        seasonNumber,
        seriesTitle,
        defaultGenre: contentItemGenres,
        defaultSubGenre: contentItemSubGenres,
        displayTitle,
        mamId,
    };
};

export const getLocalizedMetadata = (contentItem: ContentItem): MetadataLocalized => {
    const {languageMeta} = contentItem;
    const languages = getLanguages(languageMeta);

    return getLocalizedObject(languages, languageMeta);
};

export const getTerritorialMetadataLanguage = (
    countryCodes: string[],
    territorialMeta: TerritorialMetaItem[]
) => {
    const territorialMetadataObject = {} as MetadataTerritorial;

    for (const code of countryCodes) {
        const metadataTerritorialObj = territorialMeta.find(
            (item) => item.countryCode === code
        );

        territorialMetadataObject[code] = {
            rating: metadataTerritorialObj?.rating,
            captionRequired: metadataTerritorialObj?.captionsRequired,
            ratingDescriptors: metadataTerritorialObj?.ratingDescriptors,
        };
    }
    return territorialMetadataObject;
};

export const getTerritorialMetadata = (contentItem: ContentItem): MetadataTerritorial => {
    const {territorialMeta} = contentItem;
    const countryCodes: string[] = getCountryCodes(territorialMeta);

    return getTerritorialMetadataLanguage(countryCodes, territorialMeta);
};

export const setContentItemViewMetadata = (
    state: ContentItemViewMetadataState,
    action: Action<any>
) => {
    if (action.error) {
        return initialState;
    }
    const contentItem = action.payload;

    const general = getGeneralMetadata(contentItem);
    const localized = getLocalizedMetadata(contentItem);
    const territorial = getTerritorialMetadata(contentItem);
    const {allotment} = contentItem;
    return {
        general,
        id: contentItem.id,
        localized,
        allotment,
        territorial,
        isLoading: false,
    };
};

export const resetContentItemViewMetadata = () => {
    return initialState;
};

export const initialState = {
    general: {
        defaultGenre: [],
        defaultSubGenre: [],
        releaseDate: '',
        episodeNumber: 0,
        seasonNumber: 0,
        sourceRegion: '',
        roles: {
            actors: [],
            directors: [],
            producers: [],
            composers: [],
            writers: [],
            roleMeta: [],
        },
        mamId: '',
    },
    id: 0,
    localized: {},
    allotment: 0,
    territorial: {},
    isLoading: true,
};

const handlers = {
    [CONTENT_ITEM_VIEW_METADATA_SET]: setContentItemViewMetadata,
    [CONTENT_ITEM_VIEW_METADATA_RESET]: resetContentItemViewMetadata,
};

export default handleActions(handlers, initialState);
